import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Controller, useFieldArray, useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle, CheckCircle2, Plus, X } from 'lucide-react';
import { toast } from '../../hooks/useToast';
import { getErrorMessage } from '../../lib/api';
import { useAuthStore } from '../../store/authStore';
import { manufacturerService } from '../../services/manufacturer.service';
import { fieldVisitRequestService } from '../../services/field-visit-request.service';
import { Typeahead, type LookupItem } from '../../components/Typeahead';
import {
  earliestVisitDate,
  formatVisitDate,
  submissionDate,
} from '../../lib/fieldVisitDates';
import type { FieldVisitRequest } from '../../types/fieldVisit';
import {
  POS_STATUS_OPTIONS,
  SAMPLES_STATUS_OPTIONS,
  buildFieldVisitRequestSchema,
  emptyFieldVisitRequestForm,
  needsNoticeOverride,
  toRequestInput,
  type FieldVisitRequestFormValues,
} from './fieldVisitRequestSchema';

const inputClass = (invalid: boolean) => `input text-sm ${invalid ? 'border-red-500' : ''}`;

const FieldError = ({ id, message }: { id: string; message?: string }) =>
  message ? (
    <p id={id} className="mt-1 text-sm text-red-600">
      {message}
    </p>
  ) : null;

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="card space-y-4 p-4 sm:p-6">
    <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
    {children}
  </section>
);

// Field Visit Request form (ENG-750). Replaces the form on Katie's
// xceedfieldrequest.com. Reps may file from a phone, so the layout is one
// column below the `sm` breakpoint.
export const FieldVisitRequestForm = () => {
  const user = useAuthStore((state) => state.user);
  // The date shown on the form and used for the date pickers' minimum. A tab
  // left open past midnight Eastern would show yesterday, so it's refreshed
  // when the tab comes back into view and on every submit.
  const [submittedOn, setSubmittedOn] = useState(() => submissionDate());
  const refreshSubmittedOn = useCallback(() => {
    const today = submissionDate();
    setSubmittedOn(today);
    return today;
  }, []);
  // Validation always checks against the date at the moment it runs, so a
  // stale page can't let through a date the API would reject.
  const resolver = useCallback<Resolver<FieldVisitRequestFormValues>>(
    (values, context, options) =>
      zodResolver(buildFieldVisitRequestSchema(submissionDate()))(values, context, options),
    [],
  );
  const defaults = useMemo(
    () => emptyFieldVisitRequestForm(user?.division?.name ?? ''),
    [user?.division?.name],
  );

  const [manufacturers, setManufacturers] = useState<LookupItem[]>([]);
  const [created, setCreated] = useState<FieldVisitRequest | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    trigger,
    formState: { errors, isSubmitting, isSubmitted },
  } = useForm<FieldVisitRequestFormValues>({
    resolver,
    defaultValues: defaults,
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'visit_dates' });

  const noticeOverride = watch('notice_override');
  const visitDates = watch('visit_dates');
  const earliest = earliestVisitDate(submittedOn);

  useEffect(() => {
    manufacturerService
      .list({ per_page: 1000, q: { active_eq: 'true' } })
      .then((res) => setManufacturers(res.data.map((m) => ({ id: m.id, name: m.name }))))
      .catch((err) => toast.error('Could not load clients', getErrorMessage(err)));
  }, []);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshSubmittedOn();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshSubmittedOn]);

  // Turning the override on or off changes whether the dates are valid, so
  // re-check them once the user has tried to submit.
  const setOverride = (on: boolean) => {
    setValue('notice_override', on);
    if (!on) setValue('override_reason', '');
    if (isSubmitted) void trigger('visit_dates');
  };

  const onSubmit = async (values: FieldVisitRequestFormValues) => {
    try {
      const today = refreshSubmittedOn();
      const request = await fieldVisitRequestService.create(toRequestInput(values, today));
      setCreated(request);
      window.scrollTo?.({ top: 0 });
    } catch (err) {
      toast.error('Could not submit the request', getErrorMessage(err));
    }
  };

  if (created) {
    return (
      <Confirmation
        request={created}
        onAnother={() => {
          refreshSubmittedOn();
          reset(defaults);
          setCreated(null);
        }}
      />
    );
  }

  // Highlights the exception button while the four-week error is showing.
  const noticeError = isSubmitted && !noticeOverride && needsNoticeOverride(submittedOn, visitDates ?? []);

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">New field visit request</h1>
        <p className="mt-1 text-sm text-gray-600">
          Request a field visit for a client. Visits need at least four weeks' notice.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit, () => refreshSubmittedOn())} noValidate className="space-y-6">
        <Section title="Submitter">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <span className="label">Submitted by</span>
              <p className="text-sm text-gray-900">{user?.full_name ?? '—'}</p>
            </div>
            <div>
              <span className="label">Date of request</span>
              <p className="text-sm text-gray-900">{formatVisitDate(submittedOn)}</p>
            </div>
            <div>
              <label htmlFor="division_name" className="label">Division</label>
              <input
                id="division_name"
                className={inputClass(!!errors.division_name)}
                aria-invalid={!!errors.division_name}
                aria-describedby="division_name-error"
                {...register('division_name')}
              />
              <FieldError id="division_name-error" message={errors.division_name?.message} />
            </div>
            <div>
              <label htmlFor="territory" className="label">Territory</label>
              <input
                id="territory"
                className={inputClass(!!errors.territory)}
                aria-invalid={!!errors.territory}
                aria-describedby="territory-error"
                {...register('territory')}
              />
              <FieldError id="territory-error" message={errors.territory?.message} />
            </div>
          </div>
        </Section>

        <Section title="Visit overview">
          <fieldset className="space-y-3">
            <legend className="label">Requested visit dates</legend>
            <p className="text-sm text-gray-600">
              A minimum of four weeks' notice is required. Add one row per day, or set an end date
              for a run of days.
            </p>

            {fields.map((field, index) => {
              const rowErrors = errors.visit_dates?.[index];
              const start = visitDates?.[index]?.start_date;
              return (
                <div key={field.id} className="rounded-md border border-gray-200 p-3">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-start">
                    <div>
                      <label htmlFor={`visit_dates.${index}.start_date`} className="label">
                        {fields.length > 1 ? `Visit ${index + 1} date` : 'Visit date'}
                      </label>
                      <input
                        id={`visit_dates.${index}.start_date`}
                        type="date"
                        min={noticeOverride ? submittedOn : earliest}
                        className={inputClass(!!rowErrors?.start_date)}
                        aria-invalid={!!rowErrors?.start_date}
                        aria-describedby={`visit_dates.${index}.start_date-error`}
                        {...register(`visit_dates.${index}.start_date`)}
                      />
                      <FieldError
                        id={`visit_dates.${index}.start_date-error`}
                        message={rowErrors?.start_date?.message}
                      />
                    </div>
                    <div>
                      <label htmlFor={`visit_dates.${index}.end_date`} className="label">
                        End date <span className="font-normal text-gray-500">(optional)</span>
                      </label>
                      <input
                        id={`visit_dates.${index}.end_date`}
                        type="date"
                        min={start || (noticeOverride ? submittedOn : earliest)}
                        className={inputClass(!!rowErrors?.end_date)}
                        aria-invalid={!!rowErrors?.end_date}
                        aria-describedby={`visit_dates.${index}.end_date-error`}
                        {...register(`visit_dates.${index}.end_date`)}
                      />
                      <FieldError
                        id={`visit_dates.${index}.end_date-error`}
                        message={rowErrors?.end_date?.message}
                      />
                    </div>
                    {fields.length > 1 && (
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className="btn btn-secondary inline-flex items-center justify-center gap-1 text-sm sm:mt-6"
                        aria-label={`Remove visit ${index + 1}`}
                      >
                        <X className="h-4 w-4" /> Remove
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            <FieldError id="visit_dates-error" message={errors.visit_dates?.message} />

            <button
              type="button"
              onClick={() => append({ start_date: '', end_date: '' })}
              className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800"
            >
              <Plus className="h-4 w-4" /> Add another date
            </button>
          </fieldset>

          {noticeOverride ? (
            <div className="space-y-3 rounded-md border border-amber-300 bg-amber-50 p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                <div>
                  <p className="font-semibold text-amber-900">Short-notice exception</p>
                  <p className="text-sm text-amber-800">
                    Dates inside four weeks are unlocked. This request will be flagged as a
                    short-notice exception in reports.
                  </p>
                </div>
              </div>
              <div>
                <label htmlFor="override_reason" className="label">
                  Reason <span className="font-normal text-gray-500">(optional)</span>
                </label>
                <textarea
                  id="override_reason"
                  rows={2}
                  className="input text-sm"
                  {...register('override_reason')}
                />
              </div>
              <button
                type="button"
                onClick={() => setOverride(false)}
                className="text-sm font-medium text-amber-900 underline hover:text-amber-700"
              >
                Cancel the exception
              </button>
            </div>
          ) : (
            <div className={noticeError ? 'rounded-md border border-red-200 bg-red-50 p-3' : ''}>
              <button
                type="button"
                onClick={() => setOverride(true)}
                className="btn btn-secondary text-sm"
              >
                Request a short-notice exception
              </button>
              <p className="mt-1 text-xs text-gray-500">
                For visits that can't wait four weeks. Exceptions are tracked.
              </p>
            </div>
          )}

          <div>
            <span className="label">Client</span>
            <Controller
              control={control}
              name="manufacturer_id"
              render={({ field }) => (
                <Typeahead
                  items={manufacturers}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Search clients"
                  ariaLabel="Client"
                  error={errors.manufacturer_id?.message}
                />
              )}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="client_rep_name" className="label">Client representative</label>
              <input
                id="client_rep_name"
                className={inputClass(!!errors.client_rep_name)}
                aria-invalid={!!errors.client_rep_name}
                aria-describedby="client_rep_name-error"
                {...register('client_rep_name')}
              />
              <FieldError id="client_rep_name-error" message={errors.client_rep_name?.message} />
            </div>
            <div>
              <label htmlFor="client_rep_email" className="label">
                Client representative email <span className="font-normal text-gray-500">(optional)</span>
              </label>
              <input
                id="client_rep_email"
                type="email"
                inputMode="email"
                className={inputClass(!!errors.client_rep_email)}
                aria-invalid={!!errors.client_rep_email}
                aria-describedby="client_rep_email-error"
                {...register('client_rep_email')}
              />
              <FieldError id="client_rep_email-error" message={errors.client_rep_email?.message} />
            </div>
            <div>
              <label htmlFor="regional_sales_manager_name" className="label">
                Regional sales manager <span className="font-normal text-gray-500">(optional)</span>
              </label>
              <input
                id="regional_sales_manager_name"
                className="input text-sm"
                {...register('regional_sales_manager_name')}
              />
            </div>
            <div>
              <label htmlFor="regional_sales_manager_email" className="label">
                Regional sales manager email <span className="font-normal text-gray-500">(optional)</span>
              </label>
              <input
                id="regional_sales_manager_email"
                type="email"
                inputMode="email"
                className={inputClass(!!errors.regional_sales_manager_email)}
                aria-invalid={!!errors.regional_sales_manager_email}
                aria-describedby="regional_sales_manager_email-error"
                {...register('regional_sales_manager_email')}
              />
              <FieldError
                id="regional_sales_manager_email-error"
                message={errors.regional_sales_manager_email?.message}
              />
            </div>
          </div>

          <div>
            <label htmlFor="primary_objective" className="label">Primary objective</label>
            <textarea
              id="primary_objective"
              rows={3}
              className={inputClass(!!errors.primary_objective)}
              aria-invalid={!!errors.primary_objective}
              aria-describedby="primary_objective-error"
              {...register('primary_objective')}
            />
            <FieldError id="primary_objective-error" message={errors.primary_objective?.message} />
          </div>
        </Section>

        <Section title="Inventory audit">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="samples_status" className="label">Samples</label>
              <select
                id="samples_status"
                className={inputClass(!!errors.samples_status)}
                aria-invalid={!!errors.samples_status}
                aria-describedby="samples_status-error"
                {...register('samples_status')}
              >
                <option value="">Choose…</option>
                {SAMPLES_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <FieldError id="samples_status-error" message={errors.samples_status?.message} />
            </div>
            <div>
              <label htmlFor="pos_status" className="label">POS status and availability</label>
              <select
                id="pos_status"
                className={inputClass(!!errors.pos_status)}
                aria-invalid={!!errors.pos_status}
                aria-describedby="pos_status-error"
                {...register('pos_status')}
              >
                <option value="">Choose…</option>
                {POS_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <FieldError id="pos_status-error" message={errors.pos_status?.message} />
            </div>
            <div>
              <label htmlFor="samples_notes" className="label">
                Samples notes <span className="font-normal text-gray-500">(optional)</span>
              </label>
              <textarea id="samples_notes" rows={2} className="input text-sm" {...register('samples_notes')} />
            </div>
            <div>
              <label htmlFor="pos_notes" className="label">
                POS notes <span className="font-normal text-gray-500">(optional)</span>
              </label>
              <textarea id="pos_notes" rows={2} className="input text-sm" {...register('pos_notes')} />
            </div>
          </div>
          <div>
            <label className="flex items-start gap-2 text-sm text-gray-900">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-gray-300"
                aria-invalid={!!errors.samples_acknowledged}
                aria-describedby="samples_acknowledged-error"
                {...register('samples_acknowledged')}
              />
              <span>All samples must be in-house one week before the visit starts. I'll make sure they are.</span>
            </label>
            <FieldError id="samples_acknowledged-error" message={errors.samples_acknowledged?.message} />
          </div>
        </Section>

        <Section title="Accounts">
          <p className="text-sm text-gray-600">
            You don't need to list accounts now. Once the request is in, you'll add the accounts to
            visit, with a day and call time for each, from the request's page.
          </p>
        </Section>

        <div className="flex justify-end">
          <button type="submit" disabled={isSubmitting} className="btn btn-primary w-full sm:w-auto">
            {isSubmitting ? 'Submitting…' : 'Submit request'}
          </button>
        </div>
      </form>
    </div>
  );
};

const Confirmation = ({ request, onAnother }: { request: FieldVisitRequest; onAnother: () => void }) => {
  // The form is replaced by this panel, so move focus to its heading; otherwise
  // keyboard and screen reader users are left on a button that no longer exists.
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => headingRef.current?.focus(), []);

  return (
    <div className="mx-auto max-w-3xl">
      <div className="card space-y-4 p-4 sm:p-6" role="status">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="h-6 w-6 shrink-0 text-green-600" />
          <div>
            <h1 ref={headingRef} tabIndex={-1} className="text-xl font-bold text-gray-900 focus:outline-hidden">
              Request submitted
            </h1>
            <p className="text-sm text-gray-600">
              Field visit request #{request.id} for {request.manufacturer.name} is in.
            </p>
          </div>
        </div>

        {request.notice_override && (
          <p className="inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-1 text-sm font-medium text-amber-900">
            <AlertTriangle className="h-4 w-4" /> Short-notice exception
          </p>
        )}

        <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="font-medium text-gray-700">Visit dates</dt>
            <dd className="text-gray-900">
              <ul>
                {request.visit_dates.map((d) => (
                  <li key={d.id}>
                    {formatVisitDate(d.start_date)}
                    {d.end_date ? ` – ${formatVisitDate(d.end_date)}` : ''}
                  </li>
                ))}
              </ul>
            </dd>
          </div>
          {request.samples_due_by && (
            <div>
              <dt className="font-medium text-gray-700">Samples in-house by</dt>
              <dd className="text-gray-900">{formatVisitDate(request.samples_due_by)}</dd>
            </div>
          )}
          <div>
            <dt className="font-medium text-gray-700">Client representative</dt>
            <dd className="text-gray-900">{request.client_rep_name}</dd>
          </div>
          <div>
            <dt className="font-medium text-gray-700">Notice</dt>
            <dd className="text-gray-900">{request.lead_time_days} days</dd>
          </div>
        </dl>

        <button type="button" onClick={onAnother} className="btn btn-secondary w-full sm:w-auto">
          Submit another request
        </button>
      </div>
    </div>
  );
};

export default FieldVisitRequestForm;
